<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSectionPermissions } from 'dashboard/composables/useSectionPermissions';

const { t } = useI18n();

const { canEditSection } = useSectionPermissions();

const props = defineProps({
  entry: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['edit', 'delete', 'regenerate']);

const hasEmbedding = computed(() => {
  return props.entry.embedding !== null && props.entry.embedding !== undefined;
});
</script>

<template>
  <div
    class="group p-4 border border-slate-200 dark:border-slate-700 rounded-lg hover:border-green-500 dark:hover:border-green-500 transition-colors bg-white dark:bg-slate-800"
  >
    <div class="flex items-start justify-between">
      <div class="flex-1 min-w-0">
        <div class="flex items-center gap-2 mb-2 flex-wrap">
          <span
            v-if="entry.agent_bot"
            class="text-xs bg-woot-100 dark:bg-woot-900/30 text-woot-700 dark:text-woot-300 px-2 py-0.5 rounded font-medium flex items-center gap-1"
          >
            <span class="i-lucide-bot w-3 h-3" />
            {{ entry.agent_bot.name }}
          </span>
          <span
            v-if="hasEmbedding"
            class="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-0.5 rounded font-medium"
          >
            {{ t('OMNI.KNOWLEDGE_BASE.HAS_EMBEDDING') }}
          </span>
          <span
            v-else
            class="text-xs bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 px-2 py-0.5 rounded font-medium"
          >
            {{ t('OMNI.KNOWLEDGE_BASE.NO_EMBEDDING') }}
          </span>
        </div>

        <h3 class="text-sm font-bold text-slate-900 dark:text-slate-100 mb-2 line-clamp-2">
          {{ entry.question || t('OMNI.KNOWLEDGE_BASE.NO_QUESTION') }}
        </h3>

        <p class="text-xs text-slate-600 dark:text-slate-400 line-clamp-3">
          {{ entry.answer || t('OMNI.KNOWLEDGE_BASE.NO_ANSWER') }}
        </p>

        <div v-if="entry.metadata && Object.keys(entry.metadata).length > 0" class="mt-2 flex flex-wrap gap-1">
          <span
            v-for="(value, key) in entry.metadata"
            :key="key"
            class="text-xs bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-2 py-0.5 rounded"
          >
            {{ key }}: {{ value }}
          </span>
        </div>
      </div>

      <div
        v-if="canEditSection('response_templates')"
        class="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity ml-2"
      >
        <button
          class="p-1.5 text-green-500 hover:bg-green-50 dark:hover:bg-green-900/20 rounded"
          :title="t('OMNI.COMMON.EDIT')"
          @click="emit('edit', entry)"
        >
          <span class="i-lucide-edit w-4 h-4" />
        </button>
        <button
          class="p-1.5 text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
          :title="t('OMNI.KNOWLEDGE_BASE.REGENERATE_EMBEDDING')"
          @click="emit('regenerate', entry.id)"
        >
          <span class="i-lucide-refresh-cw w-4 h-4" />
        </button>
        <button
          class="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
          :title="t('OMNI.COMMON.DELETE')"
          @click="emit('delete', entry.id)"
        >
          <span class="i-lucide-trash w-4 h-4" />
        </button>
      </div>
    </div>
  </div>
</template>

